import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useMemo } from 'react'

import type { NodeOverlay, PageGraphSource } from './canvasTypes'
import type { FlowAnimationLayerProps } from './FlowAnimationLayer'
import {
  type IsolationFocus,
  type IsolationNodeRef,
  type IsolationStopBadge,
  projectIsolationHighlights,
  projectIsolationMarkers,
} from './isolationMarkers'
import { projectPageFlowPaths } from './isolationMarkup'
import { useIsGraphLocked } from './useIsGraphLocked'
import { useIsGraphModeEnabled } from './useIsGraphModeEnabled'

const noStops: IsolationStopBadge[] = []
const noHighlights: IsolationNodeRef[] = []

export function usePageFlowAnimation({
  pageGraphs,
  overlays,
  isActiveTab,
  stops = noStops,
  highlights = noHighlights,
  focus,
  isolating = false,
}: {
  pageGraphs: PageGraphSource[]
  overlays: NodeOverlay[]
  isActiveTab: boolean
  stops?: IsolationStopBadge[]
  highlights?: IsolationNodeRef[]
  focus?: IsolationFocus
  isolating?: boolean
}): FlowAnimationLayerProps | undefined {
  const locked = useIsGraphLocked()
  const graphMode = useIsGraphModeEnabled()
  const { isPipelineConfigScopeLoading } = usePipelineConfig()
  const paths = useMemo(
    () => projectPageFlowPaths(pageGraphs, overlays, stops, isolating),
    [pageGraphs, stops, isolating, overlays]
  )
  const markers = useMemo(
    () => projectIsolationMarkers(stops, overlays, focus),
    [stops, overlays, focus]
  )
  const highlighted = useMemo(
    () => projectIsolationHighlights(highlights, overlays),
    [highlights, overlays]
  )
  const visible =
    graphMode &&
    !locked &&
    !isPipelineConfigScopeLoading &&
    isActiveTab &&
    pageGraphs.length > 0
  return visible
    ? { paths, isolating, markers, highlights: highlighted }
    : undefined
}
